import { motion } from "framer-motion";
import { fadeUp, staggerContainer } from "@/lib/animations";
import SectionTitle from "../ui/section-title";
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "@/components/ui/carousel";
import { useState, useRef, useEffect } from "react";

import DayDreamers from "@assets/DayDreamers.mp4";
import DreamCar from "@assets/DreamCar.mp4";
import GugasSteakhouse from "@assets/Guga's Steakhouse .mp4";
import Riveter from "@assets/Riveter-Review-Reel.mp4";
import Puff from "@assets/PuffVid2.mp4";

// Actual reel data with video files
const reelsData = [
  {
    title: "DayDreamers: Travel Experience",
    username: "@grapegraph.ai",
    videoSrc: DayDreamers,
    bgColor: "from-purple-600 to-purple-500",
    likes: "12.4k",
    comments: "342",
    client: "DayDreamers"
  },
  {
    title: "Dream Car: Luxury Auto Experience",
    username: "@grapegraph.ai",
    videoSrc: DreamCar,
    bgColor: "from-blue-500 to-blue-400",
    likes: "15.2k",
    comments: "478",
    client: "Dream Car"
  },
  {
    title: "Guga's Steakhouse: Culinary Art",
    username: "@grapegraph.ai",
    videoSrc: GugasSteakhouse,
    bgColor: "from-red-500 to-orange-400",
    likes: "9.1k",
    comments: "213",
    client: "Guga's Steakhouse"
  },
  {
    title: "Riveter: Product Review Experience",
    username: "@grapegraph.ai",
    videoSrc: Riveter,
    bgColor: "from-green-500 to-green-400",
    likes: "8.7k",
    comments: "156",
    client: "Riveter"
  },
  {
    title: "Puff: Lifestyle Innovation",
    username: "@grapegraph.ai",
    videoSrc: Puff,
    bgColor: "from-purple-500 to-pink-500",
    likes: "11.3k",
    comments: "297",
    client: "Puff"
  }
];

// Video player component with auto-thumbnail generation
function ReelVideo({ videoSrc, thumbnailTime }: { videoSrc: string, index: number, thumbnailTime?: number }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Generate thumbnail from video
  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement) {
      // Try to load metadata to get frame as poster
      const handleLoadedMetadata = () => {
        try {
          // Create a canvas element
          const canvas = document.createElement('canvas');
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;
          
          // Draw the video frame
          const ctx = canvas.getContext('2d');
          if (ctx) {
            // After metadata is loaded, seek to a representative frame
            // Use provided thumbnail time or default to 0.5 seconds
            // For Guga's Steakhouse video, we'll use 5.0 seconds to get a good frame
            const captureTime = thumbnailTime || 0.5;
            videoElement.currentTime = captureTime;
            
            // Once we've seeked, draw the frame
            videoElement.addEventListener('seeked', function onSeeked() {
              // Draw the frame
              ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
              
              // Convert to data URL
              try {
                const dataUrl = canvas.toDataURL('image/jpeg');
                videoElement.poster = dataUrl;
              } catch (e) {
                console.error("Error generating thumbnail:", e);
              }
              
              // Remove this listener
              videoElement.removeEventListener('seeked', onSeeked);
            }, { once: true });
          }
        } catch (e) {
          console.error("Error setting up thumbnail generation:", e);
        }
      };

      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      
      return () => {
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [videoSrc, thumbnailTime]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        // Pause all other videos first
        document.querySelectorAll('video').forEach(video => {
          if (video !== videoRef.current) {
            video.pause();
          }
        });
        
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  return (
    <div 
      className="relative w-full h-full cursor-pointer" 
      onClick={togglePlayPause}
    >
      <video 
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        preload="metadata"
        playsInline
        crossOrigin="anonymous"
      >
        <source src={videoSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      
      {/* Play/Pause button overlay - only show when not playing */}
      {!isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="w-14 h-14 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center hover:bg-white/40 transition-all duration-300 border border-white/50">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-8 w-8 text-white" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path 
                fillRule="evenodd" 
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" 
                clipRule="evenodd" 
              />
            </svg>
          </div>
        </div>
      )}
      
      {/* Pause button overlay - only show when playing and hovering */}
      {isPlaying && (
        <div className="absolute inset-0 flex items-center justify-center z-10 opacity-0 hover:opacity-100 transition-opacity">
          <div className="w-14 h-14 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center hover:bg-black/40 transition-all duration-300 border border-white/30">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-8 w-8 text-white" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path 
                fillRule="evenodd" 
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm4 0a1 1 0 012 0v4a1 1 0 11-2 0V8z" 
                clipRule="evenodd" 
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ReelsCarouselSection() {
  return (
    <section id="reels" className="py-24 bg-white">
      <div className="container mx-auto px-4">
        <SectionTitle 
          title="Featured Reels" 
          subtitle="See our AI-powered content creation in action with these sample reels"
        />
        
        <motion.div 
          className="mt-12"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent>
              {reelsData.map((reel, index) => (
                <CarouselItem key={index} className="md:basis-1/3 lg:basis-1/5 xl:basis-1/5">
                  <motion.div 
                    className="p-1" 
                    variants={fadeUp}
                    custom={index}
                  >
                    {/* Instagram-style Reel */}
                    <div className="rounded-xl overflow-hidden shadow-md group cursor-pointer border border-gray-200">
                      {/* Vertical phone/reel aspect ratio is typically 9:16 */}
                      <div className={`aspect-[9/16] bg-gradient-to-b ${reel.bgColor} relative flex items-center justify-center overflow-hidden`}>
                        {/* Real video content with ReelVideo component */}
                        <ReelVideo 
                          videoSrc={reel.videoSrc} 
                          index={index} 
                          //thumbnailTime={reel.videoSrc.includes("Guga") ? 8.0 : undefined} 
                        />

                        {/* Instagram reel-style UI overlays */}
                        {/* Top section - account info */}
                        <div className="absolute top-0 left-0 right-0 p-3 flex items-center">
                          <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden mr-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-600" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-white text-xs font-medium">{reel.username}</span>
                          <button className="ml-auto text-white text-xs font-semibold px-2 py-1 rounded-md border border-white/30">
                            Follow
                          </button>
                        </div>

                        {/* Bottom section - title & engagement */}
                        <div className="absolute bottom-0 left-0 right-0 p-3">
                          <div className="text-white text-sm font-medium mb-2">
                            {reel.title}
                          </div>
                          
                          {/* Music info */}
                          <div className="flex items-center text-white/80 text-xs">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M18 3a1 1 0 00-1.196-.98l-10 2A1 1 0 006 5v9.114A4.369 4.369 0 005 14c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V7.82l8-1.6v5.894A4.37 4.37 0 0015 12c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2V3z" />
                            </svg>
                            <span className="truncate max-w-[120px]">Original Audio - GrapeGraph AI</span>
                          </div>
                        </div>

                        {/* Right side - interaction buttons */}
                        <div className="absolute right-3 bottom-20 flex flex-col space-y-4">
                          {/* Like button */}
                          <div className="flex flex-col items-center">
                            <button className="text-white">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                              </svg>
                            </button>
                            <span className="text-white text-xs mt-1">{reel.likes}</span>
                          </div>
                          {/* Comment button */}
                          <div className="flex flex-col items-center">
                            <button className="text-white">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                            </button>
                            <span className="text-white text-xs mt-1">{reel.comments}</span>
                          </div>
                          {/* Share button */}
                          <div className="flex flex-col items-center">
                            <button className="text-white">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        {/* Graph pattern overlay */}
                        <div className="absolute inset-0 opacity-15 mix-blend-overlay">
                          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                              <pattern id="reelGrid" width="20" height="20" patternUnits="userSpaceOnUse">
                                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="white" strokeWidth="0.5" />
                              </pattern>
                            </defs>
                            <rect width="100%" height="100%" fill="url(#reelGrid)" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex justify-center mt-8 gap-4">
              <CarouselPrevious className="static translate-y-0 bg-white" />
              <CarouselNext className="static translate-y-0 bg-white" />
            </div>
          </Carousel>
        </motion.div>
      </div>
    </section>
  );
}
